import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

interface ExecutionModeChartProps {
  data: Array<{
    mode: string;
    modeLabel: string;
    count: number;
    avgTestPassRate: number | null;
  }>;
  isLoading?: boolean;
}

export function ExecutionModeChart({ data, isLoading = false }: ExecutionModeChartProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-4 w-64 mt-1" />
        </CardHeader>
        <CardContent className="h-80">
          <div className="flex items-center justify-center h-full">
            <Skeleton className="h-64 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Filter out 'unknown' if it has 0 count or all data
  const chartData = data
    .filter((d) => d.mode !== 'unknown' || d.count > 0)
    .map((d) => ({
      ...d,
      avgTestPassRate: d.avgTestPassRate ?? 0,
    }));

  if (chartData.length === 0 || chartData.every((d) => d.count === 0)) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Execution Mode Comparison</CardTitle>
          <CardDescription>Full vs Fast-Track vs Lightweight workflows</CardDescription>
        </CardHeader>
        <CardContent className="h-80">
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
            <p className="text-sm">No execution mode data available</p>
            <p className="text-xs mt-1">Metrics will appear as issues are processed</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="chart-execution-mode">
      <CardHeader>
        <CardTitle>Execution Mode Comparison</CardTitle>
        <CardDescription>Full vs Fast-Track vs Lightweight workflows</CardDescription>
      </CardHeader>
      <CardContent className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
            <XAxis dataKey="modeLabel" />
            <YAxis yAxisId="left" orientation="left" />
            <YAxis yAxisId="right" orientation="right" domain={[0, 100]} />
            <Tooltip
              contentStyle={{
                backgroundColor: "hsl(var(--card))",
                border: "1px solid hsl(var(--border))",
                borderRadius: "0.5rem",
              }}
              formatter={(value: number, name: string) => {
                if (name === "Issue Count") return [value, name];
                if (name === "Avg Pass Rate") return [`${value.toFixed(1)}%`, name];
                return [value, name];
              }}
            />
            <Legend />
            <Bar
              yAxisId="left"
              dataKey="count"
              fill="hsl(var(--primary))"
              name="Issue Count"
            />
            <Bar
              yAxisId="right"
              dataKey="avgTestPassRate"
              fill="hsl(142, 76%, 36%)"
              name="Avg Pass Rate"
            />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
